import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { ArrowLeft, Users, MapPin, Calendar, Star, Crown, Check, X, Clock, Send, Bike, Share2, Copy, Link, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import CreateClubRideModal from '../components/CreateClubRideModal';
import EditClubRideModal from '../components/EditClubRideModal';
import EditClubModal from '../components/EditClubModal';

export default function ClubDetail({ user }) {
  const { clubId } = useParams();
  const navigate = useNavigate();
  const [club, setClub] = useState(null);
  const [members, setMembers] = useState([]);
  const [pendingMembers, setPendingMembers] = useState([]);
  const [rides, setRides] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('rides');
  const [newUpdate, setNewUpdate] = useState('');
  const [postingUpdate, setPostingUpdate] = useState(false);
  const [showCreateRide, setShowCreateRide] = useState(false);
  const [showEditClub, setShowEditClub] = useState(false);
  const [editingRide, setEditingRide] = useState(null);

  const isAdmin = club?.is_admin;
  const isMember = club?.is_member || isAdmin;

  useEffect(() => {
    fetchClubData();
  }, [clubId]);

  const fetchClubData = async () => {
    setLoading(true);
    try {
      const [clubRes, membersRes, ridesRes, updatesRes] = await Promise.all([
        axios.get(`/clubs/${clubId}`),
        axios.get(`/clubs/${clubId}/members`).catch(() => ({ data: [] })),
        axios.get(`/clubs/${clubId}/rides`).catch(() => ({ data: [] })),
        axios.get(`/clubs/${clubId}/updates`).catch(() => ({ data: [] }))
      ]);
      setClub(clubRes.data);
      setMembers(membersRes.data);
      setRides(ridesRes.data);
      setUpdates(updatesRes.data);

      if (clubRes.data.is_admin) {
        const pendingRes = await axios.get(`/clubs/${clubId}/pending-members`);
        setPendingMembers(pendingRes.data);
      }
    } catch (error) {
      console.error('Failed to load club:', error);
      toast.error(error.response?.data?.detail || 'Failed to load club');
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async () => {
    try {
      await axios.post(`/clubs/${clubId}/join`);
      toast.success('Join request sent!');
      fetchClubData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join');
    }
  };

  const handleApproveMember = async (userId) => {
    try {
      await axios.post(`/clubs/${clubId}/members/${userId}/approve`);
      toast.success('Member approved!');
      fetchClubData();
    } catch (error) {
      toast.error('Failed to approve member');
    }
  };

  const handleDeclineMember = async (userId) => {
    try {
      await axios.post(`/clubs/${clubId}/members/${userId}/decline`);
      toast.success('Request declined');
      fetchClubData();
    } catch (error) {
      toast.error('Failed to decline');
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!confirm('Remove this member from the club?')) return;
    try {
      await axios.delete(`/clubs/${clubId}/members/${userId}`);
      toast.success('Member removed');
      fetchClubData();
    } catch (error) {
      toast.error('Failed to remove member');
    }
  };

  const handlePostUpdate = async (e) => {
    e.preventDefault();
    if (!newUpdate.trim()) return;
    
    setPostingUpdate(true);
    try {
      await axios.post(`/clubs/${clubId}/updates`, { content: newUpdate });
      toast.success('Update posted!');
      setNewUpdate('');
      fetchClubData();
    } catch (error) {
      toast.error('Failed to post update');
    } finally {
      setPostingUpdate(false);
    }
  };

  // Generate invite link
  const getInviteLink = () => {
    return `${window.location.origin}/club/${clubId}`;
  };

  const handleCopyInviteLink = () => {
    navigator.clipboard.writeText(getInviteLink());
    toast.success('Invite link copied to clipboard!');
  };

  const handleShareInvite = async () => {
    const shareData = {
      title: `Join ${club?.name} on RydrLink`,
      text: `Join our cycling club "${club?.name}" on RydrLink!`,
      url: getInviteLink()
    };
    
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        // User cancelled or share failed
        handleCopyInviteLink();
      }
    } else {
      handleCopyInviteLink();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Loading club...</p>
      </div>
    );
  }

  if (!club) return null;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b border-border sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/')} data-testid="back-button">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-foreground">{club.name}</h1>
            <p className="text-sm text-muted-foreground">{club.member_count} members</p>
          </div>
          {isAdmin && (
            <Badge className="bg-purple-600"><Crown className="w-3 h-3 mr-1" />Admin</Badge>
          )}
          {isMember && !isAdmin && (
            <Badge variant="outline" className="text-green-500 border-green-500/30">
              <Check className="w-3 h-3 mr-1" />Member
            </Badge>
          )}
        </div>
      </header>

      <div className="container mx-auto px-4 py-6">
        {/* Club Info Card */}
        <Card className="mb-6" data-testid="club-info-card">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-20 h-20 rounded-lg bg-purple-600 flex items-center justify-center flex-shrink-0">
                {club.logo ? (
                  <img src={club.logo} alt={club.name} className="w-full h-full object-cover rounded-lg" />
                ) : (
                  <span className="text-3xl font-bold text-white">{club.name.charAt(0)}</span>
                )}
              </div>
              <div className="flex-1">
                <div className="flex items-start justify-between">
                  <p className="text-muted-foreground">{club.description}</p>
                  {isAdmin && (
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => setShowEditClub(true)}
                      className="ml-2 flex-shrink-0"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                {club.formatted_address && (
                  <p className="text-sm text-muted-foreground mt-2 flex items-center gap-1">
                    <MapPin className="w-4 h-4" /> {club.formatted_address}
                  </p>
                )}
              </div>
            </div>

            {!isMember && club.membership_status !== 'pending' && (
              <Button className="w-full mt-4" onClick={handleJoin} data-testid="join-club-button">
                Request to Join
              </Button>
            )}
            {club.membership_status === 'pending' && (
              <Badge variant="outline" className="w-full mt-4 justify-center py-2 text-yellow-500 border-yellow-500/30">
                <Clock className="w-4 h-4 mr-2" /> Join Request Pending
              </Badge>
            )}

            {/* Invite Section for Admin */}
            {isAdmin && (
              <div className="mt-4 p-4 bg-purple-500/10 border border-purple-500/20 rounded-lg">
                <h4 className="text-sm font-semibold text-purple-400 mb-2 flex items-center gap-2">
                  <Share2 className="w-4 h-4" /> Invite Members
                </h4>
                <div className="flex gap-2">
                  <Input 
                    value={getInviteLink()} 
                    readOnly 
                    className="text-xs bg-gray-800 border-gray-700"
                  />
                  <Button variant="outline" size="icon" onClick={handleCopyInviteLink} title="Copy link">
                    <Copy className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="icon" onClick={handleShareInvite} title="Share">
                    <Share2 className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-2">Share this link to invite cyclists to your club</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Member-only content */}
        {isMember ? (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <div className="flex items-center justify-between mb-4">
              <TabsList>
                <TabsTrigger value="rides" data-testid="rides-tab">
                  <Bike className="w-4 h-4 mr-2" />Rides
                </TabsTrigger>
                <TabsTrigger value="members" data-testid="members-tab">
                  <Users className="w-4 h-4 mr-2" />Members
                </TabsTrigger>
                <TabsTrigger value="updates" data-testid="updates-tab">
                  Updates
                </TabsTrigger>
              </TabsList>
              {isAdmin && (
                <Button onClick={() => setShowCreateRide(true)} data-testid="create-club-ride">
                  Create Club Ride
                </Button>
              )}
            </div>

            {/* Rides Tab */}
            <TabsContent value="rides">
              {/* Pending member requests for admin */}
              {isAdmin && pendingMembers.length > 0 && (
                <Card className="mb-4 border-yellow-500/30">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Clock className="w-5 h-5 text-yellow-500" />
                      Pending Join Requests ({pendingMembers.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {pendingMembers.map((member) => (
                        <div key={member.id} className="flex items-center gap-3 p-2 bg-yellow-500/10 rounded-lg">
                          <div className="w-10 h-10 bg-yellow-600 rounded-full flex items-center justify-center">
                            <span className="text-white font-bold">{member.name.charAt(0)}</span>
                          </div>
                          <div className="flex-1">
                            <p className="text-foreground font-medium">{member.name}</p>
                            <div className="flex items-center gap-1">
                              <Star className="w-3 h-3 text-yellow-500" fill="currentColor" />
                              <span className="text-xs text-muted-foreground">{member.avg_rating.toFixed(1)}</span>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" className="border-green-500 text-green-500" onClick={() => handleApproveMember(member.id)}>
                              <Check className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="outline" className="border-red-500 text-red-500" onClick={() => handleDeclineMember(member.id)}>
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {rides.length === 0 ? (
                <Card>
                  <CardContent className="p-8 text-center">
                    <Bike className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
                    <p className="text-muted-foreground">No club rides yet</p>
                    {isAdmin && (
                      <Button className="mt-4" onClick={() => setShowCreateRide(true)}>
                        Create First Ride
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-4">
                  {rides.map((ride) => (
                    <Card 
                      key={ride.id} 
                      className="cursor-pointer hover:border-purple-500/50 transition-colors"
                      onClick={() => navigate(`/ride/${ride.id}`)}
                      data-testid={`club-ride-${ride.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-foreground">{ride.distance}km {ride.category} Ride</h3>
                            <p className="text-sm text-muted-foreground flex items-center gap-2 mt-1">
                              <Calendar className="w-4 h-4" />
                              {ride.date} at {ride.time}
                            </p>
                            {ride.formatted_address && (
                              <p className="text-sm text-muted-foreground flex items-center gap-2 mt-1">
                                <MapPin className="w-4 h-4" />
                                {ride.formatted_address.split(',')[0]}
                              </p>
                            )}
                            {ride.max_riders && (
                              <p className="text-sm text-muted-foreground flex items-center gap-2 mt-1">
                                <Users className="w-4 h-4" />
                                {ride.participants_count || 0} / {ride.max_riders} riders
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {isAdmin && ride.status === 'active' && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingRide(ride);
                                }}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                            )}
                            <Badge variant={ride.status === 'completed' ? 'default' : 'secondary'}>
                              {ride.status}
                            </Badge>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Members Tab */}
            <TabsContent value="members">
              <Card>
                <CardHeader>
                  <CardTitle>Club Members ({members.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {members.map((member) => (
                      <div 
                        key={member.id} 
                        className="flex items-center gap-3 p-3 rounded-lg hover:bg-accent cursor-pointer transition-colors" 
                        onClick={() => navigate(`/profile/${member.id}`)}
                        data-testid={`member-${member.id}`}
                      >
                        <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                          {member.profile_picture ? (
                            <img src={member.profile_picture} alt={member.name} className="w-full h-full object-cover rounded-full" />
                          ) : (
                            <span className="text-white font-bold text-lg">{member.name.charAt(0)}</span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-foreground font-medium">{member.name}</p>
                          <div className="flex items-center gap-2">
                            <Star className="w-3 h-3 text-yellow-500" fill="currentColor" />
                            <span className="text-xs text-muted-foreground">{member.avg_rating?.toFixed(1) || '0.0'}</span>
                            {member.experience_level && (
                              <>
                                <span className="text-muted-foreground">•</span>
                                <span className="text-xs text-muted-foreground">{member.experience_level}</span>
                              </>
                            )}
                          </div>
                        </div>
                        {member.id === club.admin_id && (
                          <Badge className="bg-purple-600"><Crown className="w-3 h-3 mr-1" />Admin</Badge>
                        )}
                        {isAdmin && member.id !== club.admin_id && member.id !== user?.id && (
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="text-red-400 hover:text-red-300"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveMember(member.id);
                            }}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Updates Tab */}
            <TabsContent value="updates">
              {isAdmin && (
                <Card className="mb-4">
                  <CardContent className="p-4">
                    <form onSubmit={handlePostUpdate} className="space-y-3">
                      <Textarea
                        value={newUpdate}
                        onChange={(e) => setNewUpdate(e.target.value)}
                        placeholder="Post an update for your club members..."
                        rows={3}
                        data-testid="update-input"
                      />
                      <Button type="submit" disabled={postingUpdate || !newUpdate.trim()} className="w-full">
                        <Send className="w-4 h-4 mr-2" />
                        {postingUpdate ? 'Posting...' : 'Post Update'}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              )}

              {updates.length === 0 ? (
                <Card>
                  <CardContent className="p-8 text-center">
                    <p className="text-muted-foreground">No updates yet</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {updates.map((update) => (
                    <Card key={update.id} data-testid={`update-${update.id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                            <Crown className="w-5 h-5 text-white" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="font-medium text-foreground">Admin</span>
                              <span className="text-xs text-muted-foreground">
                                {new Date(update.created_at).toLocaleDateString()}
                              </span>
                            </div>
                            <p className="text-foreground whitespace-pre-wrap">{update.content}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        ) : (
          <Card>
            <CardContent className="p-8 text-center">
              <Users className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">Join this club to see rides, members, and updates</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Create Club Ride Modal */}
      {showCreateRide && (
        <CreateClubRideModal
          clubId={clubId}
          onClose={() => setShowCreateRide(false)}
          onCreated={() => {
            setShowCreateRide(false);
            fetchClubData();
          }}
        />
      )}

      {/* Edit Club Ride Modal */}
      {editingRide && (
        <EditClubRideModal
          ride={editingRide}
          onClose={() => setEditingRide(null)}
          onUpdated={() => {
            setEditingRide(null);
            fetchClubData();
          }}
          onDeleted={() => {
            setEditingRide(null);
            fetchClubData();
          }}
        />
      )}

      {/* Edit Club Modal */}
      {showEditClub && club && (
        <EditClubModal
          club={club}
          onClose={() => setShowEditClub(false)}
          onUpdated={() => {
            setShowEditClub(false);
            fetchClubData();
          }}
        />
      )}
    </div>
  );
}
